import { Dialog, type DialogRef } from '@angular/cdk/dialog';
import { Injectable, inject } from '@angular/core';

import {
  KtDsConfirmDialog,
  type KtDsConfirmDialogData,
  type KtDsConfirmDialogVariant,
} from '@shared/design-system/components/dialogs/kt-ds-confirm-dialog/kt-ds-confirm-dialog';

import { map } from 'rxjs';
import type { Observable } from 'rxjs';

export type ConfirmDialogConfig = {
  testId: string;
  title: string;
  description: string;
  /** Overrides the default `${testId}-description` on the description node. */
  descriptionTestId?: string;
  dialogVariant?: KtDsConfirmDialogVariant;
  cancelLabel: string;
  confirmLabel: string;
  /** Defaults to `${testId}-cancel`. */
  cancelTestId?: string;
  /** Defaults to `${testId}-confirm`. */
  confirmTestId?: string;
};

export type ConfirmDialogHandle = {
  /** The open dialog, for callers that must close it themselves (e.g. when their host is destroyed). */
  dialogRef: DialogRef<boolean, KtDsConfirmDialog>;
  /** Emits once when the dialog closes: `true` for confirm, `false` for cancel, Escape or a backdrop click. */
  confirmed$: Observable<boolean>;
};

/** Opens the shared `KtDsConfirmDialog` from already-translated copy. */
@Injectable({ providedIn: 'root' })
export class ConfirmDialogService {
  readonly #cdkDialog = inject(Dialog);

  /**
   * Opens a confirm dialog.
   *
   * @param config - Translated copy, test ids and optional badge variant.
   * @returns The dialog ref and an observable of the teacher's decision.
   */
  open(config: ConfirmDialogConfig): ConfirmDialogHandle {
    const dialogRef = this.#cdkDialog.open<boolean, KtDsConfirmDialogData, KtDsConfirmDialog>(KtDsConfirmDialog, {
      data: {
        testId: config.testId,
        title: config.title,
        description: config.description,
        descriptionTestId: config.descriptionTestId,
        dialogVariant: config.dialogVariant,
        cancelButton: {
          testId: config.cancelTestId ?? `${config.testId}-cancel`,
          label: config.cancelLabel,
        },
        confirmButton: {
          testId: config.confirmTestId ?? `${config.testId}-confirm`,
          label: config.confirmLabel,
        },
      },
    });

    return { dialogRef, confirmed$: dialogRef.closed.pipe(map((confirmed) => confirmed === true)) };
  }
}
